import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

export interface DropZoneHandle {
  chooseFiles: () => void
}

interface DropZoneProps {
  onFiles: (files: FileList | null) => void
}

const DropZone = forwardRef<DropZoneHandle, DropZoneProps>(function DropZone({ onFiles }, ref) {
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => ({
    chooseFiles: () => inputRef.current?.click(),
  }))

  const handleChoose = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleChoose()
      }
    },
    [handleChoose],
  )

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current += 1
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current -= 1
    if (dragCounter.current <= 0) {
      setIsDragging(false)
      dragCounter.current = 0
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      dragCounter.current = 0
      onFiles(e.dataTransfer.files)
    },
    [onFiles],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFiles(e.target.files)
      // Reset the input so the same file can be selected again.
      e.target.value = ''
    },
    [onFiles],
  )

  // Keep the file input disabled from the tab order; the dropzone itself is the single control.
  useEffect(() => {
    const input = inputRef.current
    if (input) {
      input.tabIndex = -1
    }
  }, [])

  return (
    <div
      className={`drop ${isDragging ? 'drop-active' : ''}`}
      role="button"
      tabIndex={0}
      aria-label="Drop HEIC or HEIF files here, or press Enter to choose files"
      onClick={handleChoose}
      onKeyDown={handleKeyDown}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div>
        <p>Drop HEIC or HEIF files here</p>
        <span className="button" aria-hidden="true">
          Choose files
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".heic,.heif,image/heic,image/heif"
        multiple
        className="sr-only"
        aria-label="Choose HEIC or HEIF files"
        onChange={handleChange}
      />
    </div>
  )
})

export default DropZone
